import React from 'react';
import { Lock, Crown, Check, Sparkles, ArrowRight, PackageSearch, Globe } from 'lucide-react';
import { useT } from '../context/LanguageContext';

const FEATURES = {
  boekhouden: {
    icon: Crown,
    badge: 'PRO',
    title: 'Ontgrendel de volledige boekhouding',
    tease: 'Stop met handmatig BTW uitrekenen in Excel. Laat MonteurCentraal het zware werk doen.',
    bullets: [
      'Debiteurenbeheer met ouderdomsanalyse (aging)',
      'Kosten & inkoopfacturen met automatische BTW',
      'BTW-aangifte & winst-/verliesrekening per kwartaal',
      'Grootboek + balans — automatisch dubbel boekhouden',
      'Automatische betalingsherinneringen',
      'Bankimport (MT940 / CAMT.053) — letter facturen in 1 klik af',
    ],
    blurb: 'Bespaar elk kwartaal uren werk en stuur je boekhouder met 1 klik een compleet rapport.',
  },
  onlineafspraken: {
    icon: Sparkles,
    badge: 'PRO',
    title: 'Laat klanten zelf afspraken boeken',
    tease: 'Een eigen boekingspagina die 24/7 doorwerkt — ook als jij onder een auto ligt.',
    bullets: [
      'Deelbare boekingslink voor je website & social media',
      'Klanten boeken 24/7 zelf — minder telefoontjes',
      'Slots automatisch berekend op jouw openingstijden',
      'Diensten met eigen duur (APK, onderhoud, banden…)',
      'Automatische bevestigings- en herinneringsmails',
      'RDW-kentekencheck al ingebouwd in het formulier',
    ],
    blurb: 'Garages met online boeken vullen hun agenda gemiddeld voller — zonder extra werk.',
  },
  diagnose: {
    icon: Sparkles,
    badge: 'PRO',
    title: 'AI leest je diagnoserapport voor je uit',
    tease: 'Upload een Autel-diagnoserapport en laat AI er direct een werkbon van maken — met uitleg in gewoon Nederlands.',
    bullets: [
      'Sleep de PDF erin — klaar in seconden',
      'Foutcodes (DTC\'s) automatisch herkend en uitgelegd',
      'Klant + voertuig automatisch aangemaakt of gekoppeld',
      'Werkbon met alle foutcodes als regels — direct factureerbaar',
      'Uitleg in begrijpelijke taal voor jouw klant',
      'Origineel rapport bewaard bij de documenten',
    ],
    blurb: 'Verander een onleesbaar diagnoserapport in een actiegerichte werkbon — zonder typewerk.',
  },
  advertentie: {
    icon: Sparkles,
    badge: 'PRO',
    title: 'Laat AI je verkoopadvertentie maken',
    tease: 'Vul een kenteken in en AI schrijft direct een verkoopklare advertentie — voor Marktplaats, AutoScout24 én Facebook.',
    bullets: [
      'Advertentietekst in 3 versies (Marktplaats / AutoScout24 / Facebook)',
      'Automatische waardebepaling + vraagprijs-meter (goedkoop ↔ duur)',
      'RDW-data inbegrepen — APK-datum staat automatisch in de tekst',
      'Foto-analyse: AI herkent velgen, trekhaak, schade en staat',
      'Verkoopscore, verwachte verkooptijd en concurrentie-indicatie',
      'Delen via WhatsApp + exporteren als PDF',
    ],
    blurb: 'Typ nooit meer 3 keer dezelfde advertentie — verkoop je voorraad sneller.',
  },
  onderdelenhandel: {
    icon: PackageSearch,
    badge: 'PRO',
    title: 'Onderdelenhandel — beheer, waardeer en verkoop onderdelen',
    tease: 'Beheer gebruikte én nieuwe onderdelen, sloopauto\'s en advertenties vanuit één systeem.',
    bullets: [
      'Onderdelenkaart met OEM/nummer, conditie, locatie, prijzen en foto\'s',
      'Sloopauto markeren → automatisch een complete onderdelenlijst genereren',
      'Voorraadwaarde, verwachte waarde en verkochte onderdelen in één oogopslag',
      'Snel zoeken op kenteken, OEM, nummer, merk of model',
      'Binnenkort: AI-waardebepaling, past-op-analyse en onderdeel-advertenties',
    ],
    blurb: 'Perfect voor slopers, onderdelenhandelaren en hobbygarages.',
  },
  showroom: {
    icon: Globe,
    badge: 'PRO',
    title: 'Je eigen publieke showroom',
    tease: 'Publiceer auto\'s en onderdelen met één klik naar je eigen verkooppagina — geen webshop of hosting nodig.',
    bullets: [
      'Eigen link: monteurcentraal.com/showroom/jouw-garage',
      'Occasions én onderdelen met foto\'s, specs en AI-tekst',
      'Knoppen voor WhatsApp, bellen, e-mail en afspraak maken',
      'Statussen: concept, gepubliceerd, gereserveerd, verkocht',
      'Statistieken: bezoekers, meest bekeken, contactaanvragen',
    ],
    blurb: 'Meer zichtbaarheid en leads — zonder technische kennis.',
  },
};

export default function UpgradeGate({ feature, onNavigate }) {
  const { t } = useT();
  const cfg = FEATURES[feature] || FEATURES.boekhouden;
  const Icon = cfg.icon;

  const goPricing = () => window.open('/pricing', '_blank');

  return (
    <div className="mc-gate" data-testid={`upgrade-gate-${feature}`}>
      <div className="mc-gate-card">
        {/* Vervaagde preview — "zien wat je mist" */}
        <div className="mc-gate-preview" data-testid="upgrade-gate-preview" aria-hidden="true">
          {feature === 'onlineafspraken' ? <AgendaPreview /> : <BoekhoudingPreview />}
          <div className="mc-gate-preview-fade" />
          <div className="mc-gate-preview-lock"><Lock size={22} /></div>
        </div>

        <div className="mc-gate-icon"><Icon size={28} /></div>
        <div className="mc-gate-badge"><Crown size={12} /> {t('Alleen met licentie', 'Alleen met licentie')} · {cfg.badge}</div>
        <h1 className="mc-gate-title">{t(cfg.title, cfg.title)}</h1>
        <p className="mc-gate-tease">{t(cfg.tease, cfg.tease)}</p>

        <ul className="mc-gate-list">
          {cfg.bullets.map((b, i) => (
            <li key={i} style={{ animationDelay: `${80 + i * 60}ms` }}>
              <Check size={15} /> <span>{t(b, b)}</span>
            </li>
          ))}
        </ul>

        <div className="mc-gate-blurb"><Sparkles size={14} /> {t(cfg.blurb, cfg.blurb)}</div>

        <button className="mc-gate-cta" data-testid="upgrade-gate-cta" onClick={goPricing}>
          {t('Upgrade naar PRO', 'Upgrade naar PRO')} <ArrowRight size={16} />
        </button>
        <button className="mc-gate-secondary" data-testid="upgrade-gate-back" onClick={() => onNavigate && onNavigate('dashboard')}>
          {t('Terug naar dashboard', 'Terug naar dashboard')}
        </button>
      </div>

      <style>{`
        .mc-gate { display: flex; align-items: flex-start; justify-content: center; padding: 32px 20px; }
        .mc-gate-card {
          position: relative; max-width: 560px; width: 100%;
          background: linear-gradient(160deg, #0f172a 0%, #1e293b 100%);
          border: 1px solid rgba(148,163,184,0.16); border-radius: 18px;
          padding: 22px 38px 34px; color: #e2e8f0;
          box-shadow: 0 24px 60px rgba(2,6,23,0.45);
          overflow: hidden;
        }
        .mc-gate-card::before {
          content: ''; position: absolute; top: -60px; right: -60px; width: 200px; height: 200px;
          background: radial-gradient(circle, rgba(245,158,11,0.22), transparent 70%); pointer-events: none;
        }
        /* Preview */
        .mc-gate-preview { position: relative; height: 150px; border-radius: 12px; overflow: hidden;
          margin-bottom: 22px; border: 1px solid rgba(148,163,184,0.14); background: #f8fafc; }
        .mc-gate-preview > .mc-prev-ui { filter: blur(2.5px); transform: scale(1.02); opacity: 0.92; pointer-events: none; }
        .mc-gate-preview-fade { position: absolute; inset: 0;
          background: linear-gradient(180deg, rgba(15,23,42,0.05) 0%, rgba(15,23,42,0.55) 100%); }
        .mc-gate-preview-lock { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
          width: 48px; height: 48px; border-radius: 14px; background: rgba(15,23,42,0.82); color: #fbbf24;
          display: flex; align-items: center; justify-content: center; box-shadow: 0 8px 22px rgba(0,0,0,0.4);
          backdrop-filter: blur(2px); }
        /* faux boekhouding UI */
        .mc-prev-ui { padding: 12px 14px; font-family: inherit; }
        .mc-prev-kpis { display: flex; gap: 8px; margin-bottom: 10px; }
        .mc-prev-kpi { flex: 1; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 10px; }
        .mc-prev-kpi .l { font-size: 9px; color: #94a3b8; text-transform: uppercase; }
        .mc-prev-kpi .v { font-size: 16px; font-weight: 800; color: #0f172a; }
        .mc-prev-bars { display: flex; align-items: flex-end; gap: 6px; height: 46px; padding: 0 2px; }
        .mc-prev-bars span { flex: 1; background: linear-gradient(180deg,#2563eb,#1e40af); border-radius: 3px 3px 0 0; }
        .mc-prev-rows { margin-top: 8px; }
        .mc-prev-row { display: flex; justify-content: space-between; font-size: 11px; color: #334155;
          padding: 4px 0; border-bottom: 1px solid #eef2f7; }
        /* faux agenda UI */
        .mc-prev-agenda { display: grid; grid-template-columns: 44px 1fr; gap: 0 8px; }
        .mc-prev-time { font-size: 10px; color: #94a3b8; text-align: right; padding-top: 2px; height: 26px; }
        .mc-prev-slot { height: 22px; border-radius: 6px; margin-bottom: 4px; border: 1px dashed #cbd5e1; background: #fff; }
        .mc-prev-slot.b { border: none; background: linear-gradient(135deg,#10b981,#059669); }
        .mc-prev-slot.b2 { border: none; background: linear-gradient(135deg,#2563eb,#1e40af); }
        .mc-gate-icon { width: 56px; height: 56px; border-radius: 16px;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: #fff;
          display: flex; align-items: center; justify-content: center; margin-bottom: 14px;
          box-shadow: 0 10px 24px rgba(245,158,11,0.4); }
        .mc-gate-badge { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700;
          letter-spacing: 0.4px; text-transform: uppercase; color: #fbbf24;
          background: rgba(251,191,36,0.1); padding: 5px 11px; border-radius: 999px; margin-bottom: 14px; }
        .mc-gate-title { font-size: 25px; font-weight: 800; line-height: 1.15; margin: 0 0 10px; color: #fff; letter-spacing: -0.5px; }
        .mc-gate-tease { font-size: 15px; color: #94a3b8; margin: 0 0 22px; line-height: 1.55; }
        .mc-gate-list { list-style: none; padding: 0; margin: 0 0 22px; display: grid; gap: 10px; }
        .mc-gate-list li { display: flex; align-items: flex-start; gap: 10px; font-size: 14px; color: #cbd5e1;
          opacity: 0; transform: translateY(6px); animation: gateIn 420ms ease forwards; }
        .mc-gate-list li svg { color: #34d399; flex-shrink: 0; margin-top: 2px; }
        .mc-gate-blurb { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #fcd34d;
          background: rgba(251,191,36,0.08); border: 1px solid rgba(251,191,36,0.18);
          padding: 11px 14px; border-radius: 10px; margin-bottom: 22px; line-height: 1.45; }
        .mc-gate-cta { width: 100%; padding: 14px; border: none; border-radius: 11px; cursor: pointer;
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: #fff; font-size: 15px; font-weight: 700;
          display: flex; align-items: center; justify-content: center; gap: 8px;
          box-shadow: 0 8px 20px rgba(245,158,11,0.35); transition: transform 140ms ease, box-shadow 140ms ease; }
        .mc-gate-cta:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(245,158,11,0.45); }
        .mc-gate-secondary { width: 100%; margin-top: 10px; padding: 10px; background: transparent;
          border: none; color: #64748b; font-size: 13px; cursor: pointer; }
        .mc-gate-secondary:hover { color: #94a3b8; }
        @keyframes gateIn { to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

function BoekhoudingPreview() {
  return (
    <div className="mc-prev-ui">
      <div className="mc-prev-kpis">
        <div className="mc-prev-kpi"><div className="l">Omzet</div><div className="v">€ 24.180</div></div>
        <div className="mc-prev-kpi"><div className="l">BTW</div><div className="v">€ 4.290</div></div>
        <div className="mc-prev-kpi"><div className="l">Winst</div><div className="v">€ 9.640</div></div>
      </div>
      <div className="mc-prev-bars">
        {[40, 65, 52, 80, 60, 92, 70, 85].map((h, i) => (
          <span key={i} style={{ height: `${h}%` }} />
        ))}
      </div>
      <div className="mc-prev-rows">
        <div className="mc-prev-row"><span>1300 Debiteuren</span><span>€ 3.120</span></div>
        <div className="mc-prev-row"><span>1520 Voorbelasting BTW</span><span>€ 880</span></div>
      </div>
    </div>
  );
}

function AgendaPreview() {
  const times = ['08:00', '09:00', '10:00', '11:00'];
  const slots = ['b', '', 'b2', ''];
  return (
    <div className="mc-prev-ui">
      <div className="mc-prev-agenda">
        {times.map((tm, i) => (
          <React.Fragment key={i}>
            <div className="mc-prev-time">{tm}</div>
            <div className={`mc-prev-slot ${slots[i]}`} />
          </React.Fragment>
        ))}
        <div className="mc-prev-time">12:00</div>
        <div className="mc-prev-slot b" />
      </div>
    </div>
  );
}
